// @ts-strict-ignore
import moment from 'moment';
import {
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  Box,
  Icon,
  IconName,
} from '@usealloy/component-library';

import { Dot } from '~/components/dots';
import { ajaxFetch } from '~/dashboard/helpers';
import type { Notification as NotificationType } from '~/dashboard/context';
import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';

import { markReadOrUnreadButtonClass, notificationCodeTokenClass } from './constants';
import {
  NotificationContent,
  NotificationDropdownItem,
  NotificationIconWrapper,
  NotificationMetaWrapper,
  NotificationTimestamp,
  StyledNotificationToken,
  StyledTitle,
  ToggleReadUnreadText,
} from './notificationStyles';

interface UnreadIndicatorProps {
  hidden?: boolean;
}

const UnreadIndicator = ({
  hidden = false,
}: UnreadIndicatorProps) => (
  <Dot
    color="var(--cl-juicyBlue-400)"
    hidden={hidden}
    size={12}
    style={{
      flexShrink: 0,
    }}
  />
);

interface NotificationProps {
  notification: NotificationType;
  handleNavigation: (event?: React.MouseEvent) => void;
  truncateContent?: boolean;
}

export const Notification = ({
  handleNavigation,
  notification: {
    content,
    notificationId,
    timestamp,
    title,
    viewed,
  },
  truncateContent,
}: NotificationProps) => {
  const [read, setRead] = useState(viewed);
  const { getUserContext } = useContext(UserContext);

  const updateNotification = useCallback(() => {
    const body = {
      notification: {
        notificationId,
        title,
        viewed: read === false,
      },
    };
    ajaxFetch(routes.v3.notifications, 'PUT', body)
      .then(() => {
        setRead(read === false);
        getUserContext();
      });
  }, [setRead, getUserContext, notificationId, title, read]);
  return (
    <NotificationDropdownItem
      alignItems="flex-start"
      direction="row"
      gap={2}
      key={notificationId}
      onClick={event => {
        const { className } = (event.target as HTMLElement);
        if (
          typeof className === 'string'
          && !className.includes(notificationCodeTokenClass)
          && read === false
        ) {
          updateNotification();
        }
        if (
          typeof className === 'string'
          && !className.includes(markReadOrUnreadButtonClass)
          && !className.includes(notificationCodeTokenClass)
        ) {
          handleNavigation(event);
        }
      }}
    >
      <NotificationIconWrapper>
        <Icon icon={IconName.ApplicationReview} size="large" />
      </NotificationIconWrapper>
      <Box direction="column" alignItems="flex-start" gap={1}>
        <NotificationTimestamp size={1} lineHeight={3}>
          {/* the notification timestamp is stored in UTC without timezone notation, so we have to parse the absolute UTC value before converting it to a local moment */}
          {moment(moment.utc(timestamp).valueOf()).format('MMM D yyyy h:mma')}
        </NotificationTimestamp>
        <StyledTitle lineHeight={4} size={2} weight={700}>{title}</StyledTitle>
        <NotificationContent
          components={{
            code: ({ children }) => (
              <>
                {children.map(child => (
                  <StyledNotificationToken
                    key={child as string}
                    className={notificationCodeTokenClass}
                    displayContent={child as string}
                  />
                ))}
              </>
            ),
          }}
          truncateContent={truncateContent}
        >
          {content}
        </NotificationContent>
      </Box>
      {!read && truncateContent && (
        <UnreadIndicator />
      )}
      {!truncateContent && (
        <NotificationMetaWrapper direction="row" gap={3} justifyContent="flex-end">
          <ToggleReadUnreadText
            className={markReadOrUnreadButtonClass}
            lineHeight={3}
            onClick={updateNotification}
          >
            {`Mark as ${read ? 'Unread' : 'Read'}`}
          </ToggleReadUnreadText>
          <UnreadIndicator hidden={read} />
        </NotificationMetaWrapper>
      )}
    </NotificationDropdownItem>
  );
};
